/* eslint max-lines: off */
/* eslint-disable consistent-return */
import parse from "date-fns/parse";
import format from "date-fns/format";
import difference from "lodash/difference";
import i18next from "i18next";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import {
    dateTimeFormat,
    dateFormat,
    timeFormat,
    payloadDateFormat,
    payloadTimeFormat,
    payloadShortTimeFormat,
    dateFormatWithDots,
    dateTimeWithoutSeconds
} from "../constants";

/* eslint-disable prefer-named-capture-group */

export function parseDateAndTimeToUser(dateTime: string): Date {
    return parse(dateTime, dateTimeFormat, new Date());
}

export function parseDateToUser(date: string): Date {
    // eslint-disable-next-line no-warning-comments
    // Todo handle invalid date
    return parse(date, dateFormat, new Date());
}

export const calculateNumberOfDays = (startDate: string, endDate: string): number =>
    differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1;

export function parseTimeToUser(time: string): Date {
    let resTime = "";

    if (time) {
        if (/[012][0-9]:[0-5][0-9]:[0-5][0-9]/u.test(time)) {
            resTime = time.substr(0, time.lastIndexOf(":"));
        } else if (/[012][0-9]:[0-5][0-9]/u.test(time)) {
            resTime = time;
        }

        return parse(resTime, timeFormat, new Date());
    }

    return new Date();
}

export function parseISOToDateTime(time: string): string {
    return format(new Date(time), dateTimeWithoutSeconds);
}

export function convertTimeFormat(value: string): string {
    const timeFormat = i18next.t("timeFormat") !== "timeFormat" ? i18next.t("timeFormat") : "";

    if (!timeFormat || !value || value === "24:00" || !/[012][0-9]:[0-5][0-9]/u.test(value)) {
        return value;
    }

    const parsedDate = parse(value, "HH:mm", new Date());
    const formattedTime = format(parsedDate, timeFormat);

    return formattedTime;
}

export function parseTextTimeToUser(time: string): string {
    return format(parse(time, payloadTimeFormat, new Date()), timeFormat);
}

export function parseDateToPayload(date: Date): string {
    return format(date, payloadDateFormat);
}

export const parseDateWithDots = (date: Date): string => format(date, dateFormatWithDots);

export function parseTimeToPayload(time: Date): string {
    return format(time, payloadTimeFormat);
}

export function parseShortTimeToPayload(time: Date): string {
    return format(time, payloadShortTimeFormat);
}

export function validateTimeRange(startTime: Date, endTime: Date): boolean {
    if (parseTimeToPayload(startTime) > parseTimeToPayload(endTime)) {
        return false;
    }

    return true;
}

export const getCurrentTime = (timeFormat = payloadTimeFormat): string => {
    const now = new Date();

    return format(now, timeFormat);
};

export const isTimeWithinRange = (time: string, startTime: string, endTime: string): boolean => {
    const timeDate = parse(time, payloadTimeFormat, new Date());
    const startTimeDate = parse(startTime, payloadTimeFormat, new Date());
    const endTimeDate = parse(endTime, payloadTimeFormat, new Date());

    return isAfter(timeDate, startTimeDate) && isBefore(timeDate, endTimeDate);
};

export enum daysOfWeek {
    mo = 0,
    tu = 1,
    we = 2,
    th = 3,
    fr = 4,
    sa = 5,
    su = 6
}

export const days: { [key: string]: string } = {
    fr: "Fri",
    mo: "Mon",
    sa: "Sat",
    su: "Sun",
    th: "Thu",
    tu: "Tue",
    we: "Wed"
};

export function parseDayValuesToString(array: Array<daysOfWeek>): string {
    let result = "";

    if (array.length === 0) {
        return result;
    }
    if (array.length === 1) {
        result += days[array[0]];
    } else {
        result += `${days[array[0]]} - ${days[array[array.length - 1]]}`;
    }

    return result;
}

// For Browser compatibility issue [ safari browser]

/*
 * The "g" at the end of our regular expression is called a "global modifier".
 *  This means replace will replace all copies of the matched string.
 */

export const formatDate = (date: string): string =>
    // eslint-disable-next-line require-unicode-regexp
    format(new Date(date.replace(/-/g, "/")), "yyyy-MM-dd");

// Use this to convert a string to date type and return the current date on passing the empty string
export const calculateDateToNewDate = (date: string): Date => {
    const parsedDate = date;

    if (parsedDate) {
        const [year, month, day] = parsedDate.split("-");
        const jsDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

        return jsDate;
    }

    return new Date();
};

// Converts string to date type and returns undefined on passing empty string
export const calculateDate = (date: string): Date | undefined => {
    const parsedDate = date;

    if (parsedDate) {
        const [year, month, day] = parsedDate.split("-");
        const jsDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

        return jsDate;
    }
};

const allDaysInWeek = ["mo", "tu", "we", "th", "fr", "sa", "su"];

/* eslint max-statements: [2, 33] */
export const getKeyOfMatchedValue = (obj: { [key: string]: string[] }, value: string[]): string => {
    let dayValue = "";
    let match = true;
    let idx2 = -1;
    const twoArr: string[][] = [[]];

    dayValue =
        Object.keys(obj).find(
            (key) =>
                difference(obj[key], value).length === 0 && difference(value, obj[key]).length === 0
        ) || "";

    if (dayValue === "") {
        let consecutiveDays: string[] = [];

        for (let idx = 0; idx < value.length; idx++) {
            const day = value[idx];

            if (match) {
                idx2 = allDaysInWeek.indexOf(day);
                consecutiveDays.push(day);
                match = false;
                if (idx === value.length - 1) {
                    twoArr.push(consecutiveDays);
                }
                // eslint-disable-next-line no-continue
                continue;
            }
            if (day === allDaysInWeek[idx2 + consecutiveDays.length]) {
                match = false;
                consecutiveDays.push(day);
            } else {
                match = true;
                twoArr.push(consecutiveDays);
                /* eslint-disable no-plusplus */
                idx--;
                consecutiveDays = [];
            }
            if (idx === value.length - 1) {
                twoArr.push(consecutiveDays);
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        twoArr.forEach((i: any) => {
            if (i.length) {
                if (dayValue === "") {
                    dayValue += parseDayValuesToString(i);
                } else {
                    dayValue += `,${parseDayValuesToString(i)}`;
                }
            }
        });
    }

    return dayValue;
};

export const parseDaysforPayload: { [key: string]: string[] } = {
    All: allDaysInWeek,
    "Mon - Sun": allDaysInWeek,
    "Mon-Fri": ["mo", "tu", "we", "th", "fr"],
    Fri: ["fr"], // eslint-disable-line sort-keys
    "Sat-Sun": ["sa", "su"],
    Mon: ["mo"], // eslint-disable-line sort-keys
    Sat: ["sa"],
    Sun: ["su"],
    Thu: ["th"],
    Tue: ["tu"],
    Wed: ["we"]
};

export const getDaysOfWeekShorthandNotation = (days: string[]): string => {
    const shorthandDays = days.map((day) => day.slice(0, 2).toLowerCase());
    const day = getKeyOfMatchedValue(parseDaysforPayload, shorthandDays);

    const dayValue = day === "All" ? "Mon - Sun" : day;

    return dayValue;
};

export const parseDaysOfWeekPayload = (daysOfWeek: string): string[] => {
    let dOW = parseDaysforPayload[daysOfWeek];

    if (!dOW) {
        dOW = [];
        daysOfWeek.split(",").forEach((i) => {
            if (i.includes("-")) {
                const j = i.split("-");
                const startIdx = allDaysInWeek.indexOf(parseDaysforPayload[j[0].trim()][0]);
                const endIdx = allDaysInWeek.indexOf(parseDaysforPayload[j[1].trim()][0]);

                for (let index = startIdx; index <= endIdx; index++) {
                    (dOW as string[]).push(allDaysInWeek[index]);
                }
            } else {
                (dOW as string[]).push(parseDaysforPayload[i][0]);
            }
        });
    }

    return dOW;
};

// To match days returned by date-fns
export enum parseGetDayToDayNameEnum {
    su = 0,
    mo = 1,
    tu = 2,
    we = 3,
    th = 4,
    fr = 5,
    sa = 6
}

export const parseNumberToTime = (value: number): string => `${`${value}`.padStart(2, "0")}:00`;
